import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

interface AccessibilityState {
  highContrast: boolean;
  fontScale: number; // 1 = 100%, 1.25 = 125%, 1.5 = 150%
  highlightLinks: boolean;
  toggleHighContrast: () => void;
  increaseFontSize: () => void;
  decreaseFontSize: () => void;
  resetFontSize: () => void;
  toggleHighlightLinks: () => void;
}

const AccessibilityContext = createContext<AccessibilityState | undefined>(undefined);

const FONT_SCALES = [1, 1.125, 1.25, 1.375, 1.5, 1.625, 1.75, 1.875, 2];

export function AccessibilityProvider({ children }: { children: React.ReactNode }) {
  const [highContrast, setHighContrast] = useState(() => localStorage.getItem('a11y-high-contrast') === 'true');
  const [fontScale, setFontScale] = useState(() => {
    const saved = localStorage.getItem('a11y-font-scale');
    return saved ? parseFloat(saved) : 1;
  });
  const [highlightLinks, setHighlightLinks] = useState(() => localStorage.getItem('a11y-highlight-links') === 'true');

  useEffect(() => {
    document.documentElement.classList.toggle('high-contrast', highContrast);
    localStorage.setItem('a11y-high-contrast', String(highContrast));
  }, [highContrast]);

  useEffect(() => {
    document.documentElement.style.fontSize = `${fontScale * 100}%`;
    localStorage.setItem('a11y-font-scale', String(fontScale));
  }, [fontScale]);

  useEffect(() => {
    document.documentElement.classList.toggle('highlight-links', highlightLinks);
    localStorage.setItem('a11y-highlight-links', String(highlightLinks));
  }, [highlightLinks]);

  const toggleHighContrast = useCallback(() => setHighContrast(v => !v), []);
  const toggleHighlightLinks = useCallback(() => setHighlightLinks(v => !v), []);

  const increaseFontSize = useCallback(() => {
    setFontScale(prev => {
      const idx = FONT_SCALES.indexOf(prev);
      return idx < FONT_SCALES.length - 1 ? FONT_SCALES[idx + 1] : prev;
    });
  }, []);

  const decreaseFontSize = useCallback(() => {
    setFontScale(prev => {
      const idx = FONT_SCALES.indexOf(prev);
      return idx > 0 ? FONT_SCALES[idx - 1] : prev;
    });
  }, []);

  const resetFontSize = useCallback(() => setFontScale(1), []);

  return (
    <AccessibilityContext.Provider value={{
      highContrast, fontScale, highlightLinks,
      toggleHighContrast, increaseFontSize, decreaseFontSize, resetFontSize, toggleHighlightLinks,
    }}>
      {children}
    </AccessibilityContext.Provider>
  );
}

export function useAccessibility() {
  const ctx = useContext(AccessibilityContext);
  if (!ctx) throw new Error('useAccessibility must be used within AccessibilityProvider');
  return ctx;
}
